
import { Injectable } from '@angular/core';
import { PdfDocumentRemoteControllerService } from 'core/api';
import { IPDFDocumentDto } from 'core/dto';
import { RemoteCollectionService, RemoteCollectionStore } from 'core/remotes';
import { LoaderService } from 'core/services';
import { Subject, take, takeUntil } from 'rxjs';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { SignProcessWizardService } from '../sign-process-wizard.service';
import applyEntityLoader from './utils/apply-entity-loader';

const documentEntityLoader = applyEntityLoader('document');

const initRemoteStore = (processId: string, pdfDocumentRemoteControllerService: PdfDocumentRemoteControllerService, loaderService: LoaderService<DsgDeepTranslationKey>) =>
  new RemoteCollectionStore<IPDFDocumentDto, string>({
    keyField: 'id',
    byKey: (uuid) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.getOne(uuid),
      loaderService,
      methodKey: 'by-key',
    }),
    load: () => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.getByProcess(processId),
      loaderService,
      methodKey: 'load',
    }),
    insert: (insertDto) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.create(insertDto),
      loaderService,
      methodKey: 'insert',
    }),
    update: (uuid, updateDto) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.update(uuid, updateDto),
      loaderService,
      methodKey: 'update',
    }),
    remove: (uuid) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.remove(uuid),
      loaderService,
      methodKey: 'remove',
    }),
    insertBatch: (insertDtoArray) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.insertBatch(insertDtoArray),
      loaderService,
      methodKey: 'insert',
    }),
    reorderShallow: (reorderPairs) => documentEntityLoader({
      obs: pdfDocumentRemoteControllerService.reorder(
        reorderPairs
          .sort(({ orderIdx: first }, { orderIdx: second }) => first - second)
          .map(({ key }) => key),
        processId),
      loaderService,
      subtle: true,
      methodKey: 'reorder',
    })
  })


@Injectable()
export class RemotePdfDocumentService extends RemoteCollectionService<IPDFDocumentDto> {

  private readonly _destroy = new Subject<void>()

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get processId() {
    return this.processIdSubject.value;
  }

  readonly pdfDocumentsSubject = this.localCollectionStore.stateSubject;

  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _pdfDocumentRemoteControllerService: PdfDocumentRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>
  ) {

    super();

    const processId = _signProcessWizardService.processIdSubject.value;
    const pdfDocumentsStore = initRemoteStore(processId || '', _pdfDocumentRemoteControllerService, this._loaderService);

    super.setConfig({
      remoteStore: pdfDocumentsStore,
      useLocalStore: true
    })


    this.processIdSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((processId) => {

        super.remoteStore = initRemoteStore(processId || '', this._pdfDocumentRemoteControllerService, this._loaderService)
        const alreadyLoaded = Boolean(this.pdfDocumentsSubject.value?.length);

        if (processId && !alreadyLoaded) {
          this.load({ autoSync: true }).pipe(take(1)).subscribe();
        }
      });




    this.pdfDocumentsSubject
      .subscribe((pdfDocuments) => {
        this._signProcessWizardService.pdfDocumentsSubject.next(pdfDocuments)

      })
  }


  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }

}
